import { z } from "zod";
import { isEnumValue } from "../../lib/enumGuards";
import { procedureSchema } from "./auditSession";
import { ExternalRefSchema } from "./externalControl";

// Constants
export const MAX_PROCEDURES_PER_CONTROL = 6;

// Control type enum
export const ControlType = z.enum(["bpc", "itgc", "ipe", "other"]);

// Uploaded document schema (subset of fields we need)
export const UploadedDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileType: z.string().optional(),
  fileSize: z.number().optional(),
  createdAt: z.string().optional(),
});

export type UploadedDocument = z.infer<typeof UploadedDocumentSchema>;

// Control attribute schema
export const ControlAttributeSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  statement: z.string(),
  description: z.string(),
  attributeOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ControlAttribute = z.infer<typeof ControlAttributeSchema>;

// Key Report Item Type
export const KeyReportItemTypeSchema = z.enum(["key_report", "linked_control"]);
export type KeyReportItemType = z.infer<typeof KeyReportItemTypeSchema>;

// Key Report Item schema
export const KeyReportItemSchema = z.object({
  type: KeyReportItemTypeSchema,
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
});

export type KeyReportItem = z.infer<typeof KeyReportItemSchema>;

// Control document schema
export const ControlDocumentSchema = z.object({
  id: z.string(),
  controlId: z.string().uuid(),
  documentId: z.string(),
  docType: z.enum(["screen_print", "work_paper", "other", "unknown"]),
  state: z.enum(["pending", "processing", "ready"]),
  sourceSystem: z.string(),
  documentType: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  fullDocument: UploadedDocumentSchema.nullable().optional(),
});

export type ControlDocument = z.infer<typeof ControlDocumentSchema>;

export enum ConfiguredState {
  Configured = "configured",
  NotConfigured = "not_configured",
  Draft = "draft",
}

export const isConfiguredState = (value: string): value is ConfiguredState =>
  isEnumValue(ConfiguredState, value);

export const DryControlSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  workspaceId: z.string(),
  createdBy: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  duplicatedFrom: z.string().nullable(),
  controlType: ControlType,
  ipeProcedureId: z.string().nullable(),
  isTemplate: z.boolean().default(false),
  cycle: z.string().nullable(),
  process: z.string().nullable(),
  configuredState: z
    .nativeEnum(ConfiguredState)
    .default(ConfiguredState.NotConfigured),
  controlOwner: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  externalRef: ExternalRefSchema.optional().nullable(),
});

export type DryControl = z.infer<typeof DryControlSchema>;

// Control schema
export const ControlSchema = z.object({
  ...DryControlSchema.shape,
  controlDocuments: z.array(ControlDocumentSchema).nullable().optional(),
  attributes: z.array(ControlAttributeSchema).nullable().optional(),
  keyReportItems: z.array(KeyReportItemSchema).nullable().optional(),
});

// Inferred types
export type ControlType = z.infer<typeof ControlType>;
export type Control = z.infer<typeof ControlSchema>;

export const ControlWithProcedureSchema = ControlSchema.extend({
  procedures: z.array(procedureSchema),
});

export type ControlWithProcedure = z.infer<typeof ControlWithProcedureSchema>;

export const ControlWithUserSchema = ControlSchema.extend({
  user: z
    .object({
      id: z.string(),
      email: z.string(),
      createdAt: z.string(),
    })
    .optional()
    .nullable(),
});

export type ControlWithUser = z.infer<typeof ControlWithUserSchema>;

export const FilterOptionsSchema = z.object({
  cycles: z.array(z.string()),
  processes: z.array(z.string()),
  controlOwners: z.array(z.string()),
  configuredStates: z.array(z.string()),
  testCreators: z.array(z.string()).optional().default([]),
  fyCycles: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .optional()
    .default([]),
});

export type FilterOptions = z.infer<typeof FilterOptionsSchema>;
